import { useState } from "react";
import { toast } from "sonner";
import { Check, RefreshCw, ExternalLink, Loader2, FileSearch } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { StatusBadge } from "../StatusBadge";
import { Input } from "../ui/input";
import { FactStatus, FactDetails } from "./FactCheck";
import { FixFailedButton, FixReport } from "./FixFailedButton";
import { ReplaceVenueButton } from "./ReplaceVenueButton";

const Row = ({ kit, it, reload }) => {
  const [url, setUrl] = useState(it.url || "");
  const [busy, setBusy] = useState(false);
  const patch = async (body, msg) => {
    setBusy(true);
    try {
      await http.patch(`/kits/${kit.id}/verification/${it.id}`, body);
      if (msg) toast.success(msg);
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <tr data-testid={`verify-row-${it.id}`} className="border-b border-stone-200 align-top">
      <td className="p-3">
        <div className="text-[#2B2A26] font-medium">{it.name}</div>
        <div className="text-xs text-stone-500">{it.section_title}</div>
      </td>
      <td className="p-3 min-w-[260px]">
        <div className="flex gap-2">
          <Input data-testid={`verify-url-input-${it.id}`} value={url} onChange={(e) => setUrl(e.target.value)} className="h-9 bg-[#F7F5F0] text-xs font-mono-ui" />
          {url && <a href={/^https?:/.test(url) ? url : `https://${url}`} target="_blank" rel="noreferrer" className="p-2 text-stone-500 hover:text-stone-900"><ExternalLink size={14} /></a>}
        </div>
        {it.verify_note && <div className="text-xs text-stone-500 mt-1.5">AI note: {it.verify_note}</div>}
      </td>
      <td className="p-3">
        <StatusBadge status={it.status} testId={`verify-status-${it.id}`} />
        <div className="text-xs text-stone-500 mt-1">{it.note}</div>
        <div className="text-[10px] font-mono-ui text-stone-600 mt-0.5">confidence: {it.confidence}</div>
      </td>
      <td className="p-3 min-w-[240px]">
        <FactStatus it={it} testId={`fact-status-${it.id}`} />
        <FactDetails kit={kit} it={it} reload={reload} />
      </td>
      <td className="p-3">
        <div className="flex gap-2 justify-end">
          <button data-testid={`verify-recheck-${it.id}`} disabled={busy} onClick={() => patch({ url }, "Re-checked")} className="inline-flex items-center gap-1 rounded-sm border border-stone-300 px-3 py-1.5 text-xs text-stone-600 hover:text-stone-900 hover:bg-stone-100">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {url !== (it.url || "") ? "Save & check" : "Re-check"}
          </button>
          <button data-testid={`verify-approve-${it.id}`} disabled={busy || it.status === "failed"} title={it.status === "failed" ? "Failed items must be fixed or replaced" : ""} onClick={() => patch({ approved: !it.approved })}
            className={`inline-flex items-center gap-1 rounded-sm px-3 py-1.5 text-xs border transition-colors ${it.approved ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-stone-300 text-stone-600 hover:text-stone-900 hover:bg-stone-100"}`}>
            <Check size={12} /> {it.approved ? "Approved" : "Approve"}
          </button>
          <ReplaceVenueButton kit={kit} it={it} reload={reload} />
        </div>
      </td>
    </tr>
  );
};

export const VerificationTab = ({ kit, reload }) => {
  const [filter, setFilter] = useState("issues");
  const [busy, setBusy] = useState(false);
  const items = kit.verification || [];
  const isIssue = (i) => i.status === "failed" || (!i.approved && (i.status !== "ok" || i.fact_status === "mismatch"));
  const shown = items.filter((i) => filter === "all" || (filter === "issues" ? isIssue(i) : filter === "mismatch" ? i.fact_status === "mismatch" : i.status === filter));
  const factcheck = async () => {
    try { await http.post(`/kits/${kit.id}/factcheck`); toast.success("Fact-check started — reading official websites"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const rerun = async () => {
    setBusy(true);
    try { await http.post(`/kits/${kit.id}/verify`); toast.success("All websites re-checked"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const c = kit.summary.counts;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        {[["issues", `Needs review (${items.filter(isIssue).length})`], ["all", `All (${items.length})`], ["ok", `Live (${c.ok})`], ["flagged", `Flagged (${c.flagged})`], ["failed", `Failed (${c.failed})`], ["mismatch", `Fact mismatches (${kit.summary.facts?.mismatch || 0})`]].map(([k, l]) => (
          <button key={k} data-testid={`verify-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-sm px-4 py-1.5 text-sm border transition-colors ${filter === k ? "border-[#C9A847] text-[#9A7B2C] bg-[#C9A847]/10" : "border-stone-300 text-stone-500 hover:text-stone-900"}`}>{l}</button>
        ))}
        <FixFailedButton kit={kit} count={items.filter(isIssue).length} reload={reload} className="ml-auto" />
        <button data-testid="factcheck-run-button" onClick={factcheck} className={`${items.some(isIssue) || kit.status === "fixing" ? "" : "ml-auto "}inline-flex items-center gap-2 rounded-sm border border-[#C9A847]/40 px-4 py-1.5 text-sm text-[#9A7B2C] hover:bg-[#C9A847]/10`}>
          <FileSearch size={14} /> Run fact-check
        </button>
        <button data-testid="verify-rerun-all" onClick={rerun} disabled={busy} className="ml-0 inline-flex items-center gap-2 rounded-sm border border-stone-300 px-4 py-1.5 text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-100">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-check all
        </button>
      </div>
      <FixReport report={kit.fix_report} />
      <div className="rounded-xl border border-stone-200 bg-white overflow-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left eyebrow"><th className="p-3">Venue</th><th className="p-3">Official website</th><th className="p-3">Website</th><th className="p-3">Price · hours · phone</th><th className="p-3" /></tr></thead>
          <tbody>{shown.map((it) => <Row key={it.id + it.url + it.status} kit={kit} it={it} reload={reload} />)}</tbody>
        </table>
        {shown.length === 0 && <div data-testid="verify-empty" className="p-10 text-center text-stone-500">Nothing to review here — the gate is clean.</div>}
      </div>
    </div>
  );
};
